import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon, PencilSquareIcon, CheckCircleIcon, ArrowUturnLeftIcon,
  DocumentArrowUpIcon, DocumentTextIcon, TrashIcon, ArrowDownTrayIcon, ChatBubbleLeftRightIcon,
  BanknotesIcon, PhotoIcon, UserIcon, TagIcon, ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import {
  STATI, StatoPill, prossimoStato, calcolaProvvigioni, prezzoNegozioConIva,
  formatEuro, formatData, formatDataOra, clienteNome, clienteIniziali, metodoPagamentoLabel
} from './statiPratica';

const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5';
const inputCls = 'w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all';
const cardCls = 'bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50';

// Card in stile NLT (header grigio con icona + corpo)
function Card({ title, icon: Icon, action, children, className = '' }) {
  return (
    <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
      <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          {Icon && <Icon className="h-5 w-5 mr-2 text-emerald-600" />}{title}
        </h3>
        {action}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// Coppia etichetta/valore in stile NLT
function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-500">{label}:</label>
      <p className="text-sm font-medium text-gray-900">{children || 'Non specificato'}</p>
    </div>
  );
}

// Barra tab in stile NLT (rounded-t, attiva emerald)
function TabBar({ tab, setTab, tabs }) {
  return (
    <div className="flex flex-wrap gap-1">
      {tabs.map(t => (
        <button key={t.key} onClick={() => setTab(t.key)}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          {t.label.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function BikePraticaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pratica, setPratica] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('riepilogo');
  const [azioneStato, setAzioneStato] = useState(false);
  const [percProvv, setPercProvv] = useState(30);
  const fileRef = useRef();

  // Ruolo utente: i non-admin non vedono percentuale/costo d'acquisto sulle provvigioni
  const isAdmin = (() => {
    try { const u = JSON.parse(localStorage.getItem('user')); return String(u?.role || u?.ruolo || '').toLowerCase() === 'admin'; }
    catch (e) { return false; }
  })();

  const carica = useCallback(async () => {
    try { const { data } = await api.get(`/bici-pratiche/${id}`); setPratica(data); }
    catch (e) { toast.error('Pratica non trovata'); navigate('/bike/pratiche'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { if (isAdmin) api.get('/bici/impostazioni').then(({ data }) => setPercProvv(Number(data.percentualeProvvigione) || 0)).catch(() => {}); }, [isAdmin]);

  const cambiaStato = async (body) => {
    setAzioneStato(true);
    try { const { data } = await api.put(`/bici-pratiche/${id}/stato`, body); setPratica(p => ({ ...p, ...data })); toast.success('Stato aggiornato'); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore nel cambio stato'); }
    finally { setAzioneStato(false); }
  };

  if (loading) return <div className="w-full px-4 md:px-6 lg:px-8 py-16 text-center text-gray-400">Caricamento…</div>;
  if (!pratica) return null;

  const prossimo = prossimoStato(pratica.statoPratica);
  const confermata = pratica.statoPratica === 'confermata';

  const tabs = [
    { key: 'riepilogo', label: 'Riepilogo' },
    { key: 'documenti', label: `Documenti${pratica.documenti?.length ? ` (${pratica.documenti.length})` : ''}` },
    { key: 'comunicazioni', label: 'Comunicazioni' }
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      {/* Barra superiore: info pratica + tab (struttura NLT) */}
      <div className="bg-white border-b border-gray-200">
        <div className="w-full px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <button onClick={() => navigate('/bike/pratiche')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-2">
                <ArrowLeftIcon className="h-4 w-4" /> Pratiche
              </button>
              <div className="text-sm text-gray-500">Pratica: <span className="font-medium text-gray-900">{pratica.codicePratica}</span></div>
              <div className="text-sm text-gray-500">Cliente: <span className="font-medium text-gray-900">{clienteNome(pratica.cliente)}</span></div>
            </div>
            <TabBar tab={tab} setTab={setTab} tabs={tabs} />
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 lg:px-8 py-6 space-y-6">
        {/* Header pratica */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900 truncate">{clienteNome(pratica.cliente)} <span className="text-gray-400 font-normal">| ID {pratica.id}</span></h2>
            <div className="flex items-center flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-gray-600">
              <div><span className="font-medium">Numero:</span> {pratica.codicePratica}</div>
              <div className="flex items-center gap-2"><span className="font-medium">Stato:</span> <StatoPill stato={pratica.statoPratica} size="sm" /></div>
              {pratica.prezzoVendita && <div><span className="font-medium">Prezzo:</span> {formatEuro(pratica.prezzoVendita)}</div>}
              {pratica.dataConsegnaPrevista && <div><span className="font-medium">Consegna:</span> {formatData(pratica.dataConsegnaPrevista)}</div>}
            </div>
          </div>

          {/* Controllo stato (2 stati) */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {!confermata && prossimo && (
              <button onClick={() => cambiaStato({ azione: 'avanza' })} disabled={azioneStato}
                className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg font-medium shadow-lg shadow-emerald-500/25 hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50">
                <CheckCircleIcon className="h-5 w-5 mr-2" /> Conferma pratica
              </button>
            )}
            {confermata && (
              <>
                <span className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-emerald-50 text-emerald-700 rounded-lg font-medium border border-emerald-200"><CheckCircleIcon className="h-5 w-5" /> Confermata</span>
                <button onClick={() => cambiaStato({ stato: 'preventivo' })} disabled={azioneStato}
                  className="inline-flex items-center px-4 py-2.5 bg-white border border-gray-300 text-gray-600 rounded-lg font-medium hover:text-gray-900 transition-colors">
                  <ArrowUturnLeftIcon className="h-4 w-4 mr-1.5" /> Riporta a preventivo
                </button>
              </>
            )}
          </div>
        </div>

        {/* Contenuto tab */}
        {tab === 'riepilogo' && <TabRiepilogo pratica={pratica} percProvv={percProvv} isAdmin={isAdmin} onSaved={carica} />}
        {tab === 'documenti' && <TabDocumenti pratica={pratica} onChange={carica} fileRef={fileRef} />}
        {tab === 'comunicazioni' && <TabComunicazioni pratica={pratica} onChange={carica} />}
      </div>
    </div>
  );
}

// ---- Tab Riepilogo (struttura a 3 colonne come NLT) ------------------------
function TabRiepilogo({ pratica, percProvv, isAdmin, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    taglia: pratica.taglia || '', quantita: pratica.quantita || 1,
    prezzoVendita: pratica.prezzoVendita || '', acconto: pratica.acconto || '',
    metodoPagamento: pratica.metodoPagamento || '', dataConsegnaPrevista: pratica.dataConsegnaPrevista ? pratica.dataConsegnaPrevista.slice(0, 10) : '',
    note: pratica.note || '', noteInterne: pratica.noteInterne || ''
  });
  const [salvando, setSalvando] = useState(false);

  const salva = async () => {
    setSalvando(true);
    try { await api.put(`/bici-pratiche/${pratica.id}`, form); toast.success('Modifiche salvate'); setEdit(false); onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore nel salvataggio'); }
    finally { setSalvando(false); }
  };

  const c = pratica.cliente || {};
  const b = pratica.bici || {};
  const saldo = (Number(pratica.prezzoVendita) || 0) - (Number(pratica.acconto) || 0);

  const prezzoPubblicoEff = pratica.prezzoVendita != null && pratica.prezzoVendita !== '' ? pratica.prezzoVendita : b.prezzoPubblico;
  const prezzoNegozio = prezzoNegozioConIva(b.prezzoNegozio); // IVA 22% inclusa
  const haProvv = prezzoPubblicoEff != null && prezzoPubblicoEff !== '' && prezzoNegozio != null && prezzoNegozio !== '';
  const prov = calcolaProvvigioni(prezzoPubblicoEff, prezzoNegozio, percProvv);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* Colonna sinistra — Cliente */}
      <div className="space-y-6">
        <Card title="Informazioni Cliente" icon={UserIcon}>
          <div className="space-y-4">
            <Field label="Tipo cliente"><span className="capitalize">{c.tipo_cliente || 'privato'}</span></Field>
            {c.ragione_sociale
              ? <Field label="Ragione sociale">{c.ragione_sociale}</Field>
              : <><Field label="Nome">{c.nome}</Field><Field label="Cognome">{c.cognome}</Field></>}
            <Field label="Email">{c.email}</Field>
            <Field label="Telefono">{c.telefono}</Field>
            {c.codice_fiscale && <Field label="Codice fiscale">{c.codice_fiscale}</Field>}
            {(c.indirizzo || c.citta) && <Field label="Indirizzo">{[c.indirizzo, c.cap, c.citta, c.provincia].filter(Boolean).join(', ')}</Field>}
          </div>
        </Card>
      </div>

      {/* Colonna centrale — Prodotto */}
      <div className="space-y-6">
        <Card title="Prodotto" icon={TagIcon}>
          {b.id ? (
            <div>
              <div className="w-full h-44 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center overflow-hidden mb-4">
                {b.immagine?.path_file
                  ? <img src={b.immagine.path_file} alt={b.modello || 'Bici'} className="h-full w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                  : <PhotoIcon className="h-12 w-12 text-gray-300" />}
              </div>
              <p className="text-xs uppercase tracking-wide text-emerald-600 font-semibold">{b.brand || 'Bici'}</p>
              <p className="text-lg font-bold text-gray-900">{b.modello || '—'}</p>
              {b.nomeProdotto && <p className="text-sm text-gray-500 mt-0.5">{b.nomeProdotto}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                {pratica.taglia && <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">Taglia {pratica.taglia}</span>}
                <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">Q.tà {pratica.quantita || 1}</span>
                {b.ean && <span className="px-2.5 py-1 bg-gray-100 text-gray-500 rounded-lg text-xs font-mono">EAN {b.ean}</span>}
                {b.arrivoPrevisto && <span className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">Arrivo: {b.arrivoPrevisto}</span>}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                <Field label="Prezzo pubblico (listino)">{formatEuro(b.prezzoPubblico)}</Field>
                {isAdmin && <Field label="Prezzo negozio (IVA incl.)">{formatEuro(prezzoNegozioConIva(b.prezzoNegozio))}</Field>}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-gray-400">
              <TagIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Nessun prodotto associato</p>
            </div>
          )}
        </Card>
      </div>

      {/* Colonna destra — Dettagli vendita + Provvigioni */}
      <div className="space-y-6">
        <Card title="Dettagli vendita" icon={ClipboardDocumentListIcon}
          action={!edit
            ? <button onClick={() => setEdit(true)} className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 font-medium"><PencilSquareIcon className="h-4 w-4" /> Modifica</button>
            : <div className="flex gap-2">
                <button onClick={() => setEdit(false)} className="text-sm text-gray-500 hover:text-gray-700">Annulla</button>
                <button onClick={salva} disabled={salvando} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">{salvando ? 'Salvataggio…' : 'Salva'}</button>
              </div>}>
          {!edit ? (
            <div className="space-y-4">
              <Field label="Prezzo di vendita">{formatEuro(pratica.prezzoVendita)}</Field>
              <Field label="Acconto">{formatEuro(pratica.acconto)}</Field>
              <div>
                <label className="text-sm font-medium text-gray-500">Saldo residuo:</label>
                <p className="text-sm font-semibold text-emerald-700">{formatEuro(saldo)}</p>
              </div>
              <Field label="Pagamento">{metodoPagamentoLabel[pratica.metodoPagamento]}</Field>
              <Field label="Consegna prevista">{formatData(pratica.dataConsegnaPrevista)}</Field>
              {pratica.note && <div><label className="text-sm font-medium text-gray-500">Note:</label><p className="text-sm text-gray-700 whitespace-pre-wrap">{pratica.note}</p></div>}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Taglia</label><input value={form.taglia} onChange={e => setForm({ ...form, taglia: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Quantità</label><input type="number" min="1" value={form.quantita} onChange={e => setForm({ ...form, quantita: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Prezzo di vendita</label><input type="number" step="0.01" value={form.prezzoVendita} onChange={e => setForm({ ...form, prezzoVendita: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>Acconto</label><input type="number" step="0.01" value={form.acconto} onChange={e => setForm({ ...form, acconto: e.target.value })} className={inputCls} /></div>
              <div>
                <label className={labelCls}>Pagamento</label>
                <select value={form.metodoPagamento} onChange={e => setForm({ ...form, metodoPagamento: e.target.value })} className={inputCls}>
                  <option value="">—</option>
                  {Object.entries(metodoPagamentoLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Consegna prevista</label><input type="date" value={form.dataConsegnaPrevista} onChange={e => setForm({ ...form, dataConsegnaPrevista: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Note</label><textarea rows="2" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2"><label className={labelCls}>Note interne</label><textarea rows="2" value={form.noteInterne} onChange={e => setForm({ ...form, noteInterne: e.target.value })} className={inputCls} /></div>
            </div>
          )}
        </Card>

        <Card title="Provvigioni" icon={BanknotesIcon}
          action={isAdmin ? <span className="text-xs text-gray-500">Agente: <span className="font-semibold text-gray-900">{prov.percentuale}%</span></span> : null}>
          {isAdmin ? (
            !haProvv ? (
              <p className="text-sm text-gray-400 py-2">Collega una bici con <b>Prezzo negozio</b> valorizzato per calcolare la provvigione.</p>
            ) : (
              <div className="space-y-4">
                <Field label="Prezzo pubblico (vendita)">{formatEuro(prezzoPubblicoEff)}</Field>
                <Field label="Prezzo negozio (IVA incl.)">{formatEuro(prezzoNegozio)}</Field>
                <Field label="Margine (100%)">{formatEuro(prov.margine)}</Field>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-lg p-4">
                  <p className="text-xs text-emerald-700/80">Provvigione agente ({prov.percentuale}%)</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-0.5">{formatEuro(prov.provvigioneAgente)}</p>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Quota negozio</span>
                  <span className="font-semibold text-gray-900">{formatEuro(prov.provvigioneAzienda)}</span>
                </div>
              </div>
            )
          ) : (
            // Non-admin: solo l'importo della provvigione (niente percentuale né costo d'acquisto)
            pratica.provvigioneAgente != null ? (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-lg p-4">
                <p className="text-xs text-emerald-700/80">La tua provvigione</p>
                <p className="text-2xl font-bold text-emerald-700 mt-0.5">{formatEuro(pratica.provvigioneAgente)}</p>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-2">Provvigione non ancora disponibile.</p>
            )
          )}
        </Card>
      </div>
    </div>
  );
}

// ---- Tab Documenti ---------------------------------------------------------
function TabDocumenti({ pratica, onChange, fileRef }) {
  const [uploading, setUploading] = useState(false);
  const documenti = pratica.documenti || [];

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/bici-pratiche/${pratica.id}/documenti`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(files.length > 1 ? `${files.length} documenti caricati` : 'Documento caricato');
      onChange();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore nel caricamento'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const scarica = async (doc) => {
    try {
      const resp = await api.get(`/bici-pratiche/${pratica.id}/documenti/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.nome_file;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error('Errore nel download'); }
  };

  const elimina = async (doc) => {
    if (!window.confirm(`Eliminare "${doc.nome_file}"?`)) return;
    try { await api.delete(`/bici-pratiche/${pratica.id}/documenti/${doc.id}`); toast.success('Documento eliminato'); onChange(); }
    catch (err) { toast.error('Errore nell\'eliminazione'); }
  };

  const kb = (n) => n ? (n > 1024 * 1024 ? (n / 1024 / 1024).toFixed(1) + ' MB' : Math.round(n / 1024) + ' KB') : '';

  return (
    <Card title="Documenti" icon={DocumentTextIcon}
      action={<label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
        <DocumentArrowUpIcon className="h-5 w-5" /> {uploading ? 'Caricamento…' : 'Carica documenti'}
        <input ref={fileRef} type="file" multiple className="hidden" onChange={upload} disabled={uploading} />
      </label>}>
      {documenti.length === 0 ? (
        <div className="py-14 text-center border-2 border-dashed border-gray-200 rounded-lg">
          <DocumentTextIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Nessun documento. Carica carta d'identità, prova d'acquisto, moduli finanziamento…</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documenti.map(doc => (
            <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0"><DocumentTextIcon className="h-5 w-5 text-emerald-600" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{doc.nome_file}</p>
                <p className="text-xs text-gray-400">{[doc.descrizione, kb(doc.dimensione_file), formatData(doc.data_caricamento)].filter(Boolean).join(' · ')}</p>
              </div>
              <button onClick={() => scarica(doc)} className="p-2 text-gray-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors" title="Scarica"><ArrowDownTrayIcon className="h-5 w-5" /></button>
              <button onClick={() => elimina(doc)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Elimina"><TrashIcon className="h-5 w-5" /></button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---- Tab Comunicazioni -----------------------------------------------------
function TabComunicazioni({ pratica, onChange }) {
  const [testo, setTesto] = useState('');
  const [invio, setInvio] = useState(false);
  const eventi = Array.isArray(pratica.storicoComunicazioni) ? [...pratica.storicoComunicazioni].reverse() : [];

  const invia = async () => {
    if (!testo.trim()) return;
    setInvio(true);
    try { await api.post(`/bici-pratiche/${pratica.id}/comunicazioni`, { testo }); setTesto(''); onChange(); }
    catch (err) { toast.error(err.response?.data?.error || 'Errore nell\'invio'); }
    finally { setInvio(false); }
  };

  return (
    <Card title="Comunicazioni" icon={ChatBubbleLeftRightIcon}>
      <div className="flex gap-2 mb-6">
        <input value={testo} onChange={e => setTesto(e.target.value)} onKeyDown={e => e.key === 'Enter' && invia()}
          placeholder="Scrivi una nota o un aggiornamento…" className={inputCls} />
        <button onClick={invia} disabled={invio || !testo.trim()} className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 whitespace-nowrap">Invia</button>
      </div>

      {eventi.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-400">Ancora nessuna comunicazione.</p>
      ) : (
        <div className="relative pl-6">
          <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-gray-200" />
          <div className="space-y-5">
            {eventi.map((ev, i) => {
              const isStato = ev.tipo === 'stato';
              return (
                <div key={i} className="relative">
                  <span className={`absolute -left-6 top-1 h-4 w-4 rounded-full border-2 border-white ${isStato ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">{ev.autore || 'Utente'}</span>
                    {isStato && <span className="text-xs text-gray-500">ha spostato la pratica in <span className="font-medium text-gray-900">{STATI[ev.a]?.label || ev.a}</span></span>}
                    <span className="text-[11px] text-gray-300 ml-auto whitespace-nowrap">{formatDataOra(ev.data)}</span>
                  </div>
                  {ev.testo && <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap bg-gray-50 rounded-lg px-3 py-2">{ev.testo}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
